import type { ReactElement, ReactNode } from 'react';

import { classNames } from '@m1work/shared-utils';

import { Heading, type HeadingSize } from '../Heading/Heading';
import { PriceTimer, type PriceTimerSize } from '../PriceTimer/PriceTimer';
import { Tag } from '../Tag/Tag';
import { Text } from '../Text/Text';

import styles from './Price.module.scss';

export interface PriceProps {
    /** Current price, already formatted (e.g. `"19.90"`). */
    amount: string;
    /** Price before the discount; shown struck through. */
    oldAmount?: string | null;
    currencySign: string;
    /** Icon before the price. */
    icon?: ReactNode;
    /** Keep the old price's space even without one, so cards in a row line up. @default false */
    reserveOldPrice?: boolean;
    /** Show the discount percentage badge. @default false */
    showDiscount?: boolean;
    /** Seconds until the discounted price ends; shows a countdown. */
    secondsLeft?: number | null;
    /** Text before the countdown (translated). */
    timerLabel?: string;
    /** Keep the countdown's space even without one. @default false */
    reserveTimer?: boolean;
    timerSize?: PriceTimerSize;
    /** Size of the amount. */
    size?: HeadingSize;
    className?: string;
}

/**
 * A price with the currency, optionally the old price, a discount badge and a countdown to the end of the offer.
 *
 * @example
 * <Price amount="19.90" oldAmount="24.90" currencySign="€" showDiscount secondsLeft={secondsLeft} timerLabel={t('offer.price-timer.label')} />
 */

export function Price({
    amount,
    oldAmount,
    currencySign,
    icon,
    reserveOldPrice,
    showDiscount,
    secondsLeft,
    timerLabel,
    reserveTimer,
    timerSize,
    size,
    className,
}: PriceProps): ReactElement {
    const discountPercent = oldAmount ? Math.round((1 - parseFloat(amount) / parseFloat(oldAmount)) * 100) : null;

    return (
        <div className={classNames(styles.price, className)}>
            {icon}

            <Heading weight="semibold" size={size} className={styles.amount}>
                {amount}{' '}
                <Text as="span" size="lg">
                    {currencySign}
                </Text>
            </Heading>

            {showDiscount && discountPercent !== null && discountPercent > 0 && (
                <Tag className={classNames(styles.discount, 'ml-1')} color="accent" size="md">
                    -{discountPercent}%
                </Tag>
            )}

            {(oldAmount || reserveOldPrice) && (
                <Text
                    as="span"
                    size="lg"
                    color="muted"
                    className={classNames(styles['old-price'], !oldAmount && styles['is-hidden'])}
                    aria-hidden={!oldAmount || undefined}
                >
                    {oldAmount ? `${oldAmount} ${currencySign}` : ' '}
                </Text>
            )}

            {secondsLeft ? (
                <PriceTimer
                    className={classNames(styles.timer, 'mt-1')}
                    secondsLeft={secondsLeft}
                    label={timerLabel}
                    size={timerSize}
                />
            ) : (
                reserveTimer && (
                    <PriceTimer
                        className={classNames(styles.timer, styles['is-hidden'], 'mt-1')}
                        secondsLeft={0}
                        label={timerLabel}
                        size={timerSize}
                    />
                )
            )}
        </div>
    );
}
